import { DateTime } from 'luxon';

export type SeriesSchedule = 'DAILY' | 'WEEKLY' | 'MONTHLY';

export class MaturityUtils {
  private static readonly STRIP_RE = /^([^-]+)-([^-]+)$/;

  public static getMaturity(date: DateTime, schedule: SeriesSchedule): string {
    switch (schedule) {
      case 'DAILY':
        return date.toFormat('ddMMMyyyy');
      case 'WEEKLY':
        const week = this.getWeekNumber(date);
        return `W${week.toString().padStart(2, '0')}${date.toFormat('MMMyyyy')}`;
      case 'MONTHLY':
      default:
        return date.toFormat('MMMyyyy');
    }
  }

  public static getMaturityFromString(dateStr: string, inputFormat: string, schedule: SeriesSchedule): string {
    const date = DateTime.fromFormat(dateStr, inputFormat, { locale: 'en' });
    if (!date.isValid) {
      throw new Error(`Invalid date string: '${dateStr}' with format '${inputFormat}'`);
    }
    return this.getMaturity(date, schedule);
  }

  public static getStripMaturity(start: DateTime, end: DateTime): string {
    return `${this.getMaturity(start, 'DAILY')}-${this.getMaturity(end, 'DAILY')}`;
  }

  public static isMaturityValid(maturity: string): boolean {
    try {
      const dtMaturities:DateTime<boolean>[] = this.maturityToDate(maturity);
      dtMaturities.forEach(dt => {
        if (!dt.isValid) {
          throw new Error(`Invalid maturity string: '${maturity}'`);
        }
      });

      return true;
    } catch {
      return false;
    }
  }

  public static maturityToDate(maturity: string): DateTime[] {
    const match = this.STRIP_RE.exec(maturity);
    if (match) {
      return [this.singleMaturityToDate(match[1]), this.singleMaturityToDate(match[2])];
    }
    return [this.singleMaturityToDate(maturity)];
  }

  private static singleMaturityToDate(maturity: string): DateTime {
    if (maturity.length === 7) {
      // e.g., "Jan2025"
      return DateTime.fromFormat(`01${maturity}`, 'ddMMMyyyy', { locale: 'en' });
    } else if (maturity.length === 9) {
      if (maturity.startsWith('W')) {
        // e.g., "W03Jan2025"
        const week = parseInt(maturity.substring(1, 3), 10);
        const base = DateTime.fromFormat(`01${maturity.substring(3)}`, 'ddMMMyyyy', { locale: 'en' });
        return base.plus({ days: (week - 1) * 7 });
      } else {
        // e.g., "01Jan2025"
        return DateTime.fromFormat(maturity, 'ddMMMyyyy', { locale: 'en' });
      }
    } else {
      throw new Error(`Invalid maturity: ${maturity}`);
    }
  }

  private static getWeekNumber(date: DateTime): number {
    return Math.ceil(date.ordinal / 7);
  }
}
